import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import {
  createApplication as createApplicationApi,
  getApplications as getApplicationsApi,
  getApplicationById as getApplicationByIdApi,
  updateApplication as updateApplicationApi,
  deleteApplication as deleteApplicationApi,
  deployApplication as deployApplicationApi,
  redeployApplication as redeployApplicationApi,
  addService as addServiceApi,
  updateService as updateServiceApi,
  deleteService as deleteServiceApi,
  deployService as deployServiceApi,
  startService as startServiceApi,
  stopService as stopServiceApi,
  restartService as restartServiceApi,
  getServiceLogs as getServiceLogsApi,
} from "../../services/Application";

// ===========================================================================
// ASYNC THUNKS - APPLICATIONS
// ===========================================================================

/**
 * Fetch all applications
 */
export const fetchApplications = createAsyncThunk(
  "applications/fetchAll",
  async (filters = {}, { rejectWithValue }) => {
    try {
      const response = await getApplicationsApi(filters);
      return response;
    } catch (error) {
      return rejectWithValue(error?.response?.data || error.message);
    }
  },
);

/**
 * Fetch single application by ID
 */
export const fetchApplicationById = createAsyncThunk(
  "applications/fetchById",
  async (appId, { rejectWithValue }) => {
    try {
      const response = await getApplicationByIdApi(appId);
      return response.data;
    } catch (error) {
      return rejectWithValue(error?.response?.data || error.message);
    }
  },
);

/**
 * Create new application
 */
export const createApplication = createAsyncThunk(
  "applications/create",
  async (data, { rejectWithValue }) => {
    try {
      const response = await createApplicationApi(data);
      return response.data;
    } catch (error) {
      return rejectWithValue(error?.response?.data || error.message);
    }
  },
);

/**
 * Update application
 */
export const updateApplication = createAsyncThunk(
  "applications/update",
  async ({ appId, data }, { rejectWithValue }) => {
    try {
      const response = await updateApplicationApi(appId, data);
      return response.data;
    } catch (error) {
      return rejectWithValue(error?.response?.data || error.message);
    }
  },
);

/**
 * Delete application
 */
export const deleteApplication = createAsyncThunk(
  "applications/delete",
  async (appId, { rejectWithValue }) => {
    try {
      await deleteApplicationApi(appId);
      return appId;
    } catch (error) {
      return rejectWithValue(error?.response?.data || error.message);
    }
  },
);

/**
 * Deploy all services in application
 */
export const deployApplication = createAsyncThunk(
  "applications/deploy",
  async (appId, { rejectWithValue }) => {
    try {
      const response = await deployApplicationApi(appId);
      return response;
    } catch (error) {
      return rejectWithValue(error?.response?.data || error.message);
    }
  },
);

/**
 * Redeploy application (tear down and recreate containers)
 */
export const redeployApplication = createAsyncThunk(
  "applications/redeploy",
  async (appId, { rejectWithValue }) => {
    try {
      const response = await redeployApplicationApi(appId);
      return response;
    } catch (error) {
      return rejectWithValue(error?.response?.data || error.message);
    }
  },
);

// ===========================================================================
// ASYNC THUNKS - SERVICES
// ===========================================================================

/**
 * Add service to application
 */
export const addService = createAsyncThunk(
  "applications/addService",
  async ({ appId, serviceData }, { rejectWithValue }) => {
    try {
      const response = await addServiceApi(appId, serviceData);
      return { appId, service: response.data };
    } catch (error) {
      return rejectWithValue(error?.response?.data || error.message);
    }
  },
);

/**
 * Update service
 */
export const updateService = createAsyncThunk(
  "applications/updateService",
  async ({ appId, serviceId, data }, { rejectWithValue }) => {
    try {
      const response = await updateServiceApi(appId, serviceId, data);
      return { appId, service: response.data };
    } catch (error) {
      return rejectWithValue(error?.response?.data || error.message);
    }
  },
);

/**
 * Delete service
 */
export const deleteService = createAsyncThunk(
  "applications/deleteService",
  async ({ appId, serviceId }, { rejectWithValue }) => {
    try {
      await deleteServiceApi(appId, serviceId);
      return { appId, serviceId };
    } catch (error) {
      return rejectWithValue(error?.response?.data || error.message);
    }
  },
);

/**
 * Deploy service
 */
export const deployService = createAsyncThunk(
  "applications/deployService",
  async ({ appId, serviceId }, { rejectWithValue }) => {
    try {
      const response = await deployServiceApi(appId, serviceId);
      return { appId, serviceId, result: response };
    } catch (error) {
      return rejectWithValue(error?.response?.data || error.message);
    }
  },
);

/**
 * Start service
 */
export const startService = createAsyncThunk(
  "applications/startService",
  async ({ appId, serviceId }, { rejectWithValue }) => {
    try {
      const response = await startServiceApi(appId, serviceId);
      return { appId, serviceId, result: response };
    } catch (error) {
      return rejectWithValue(error?.response?.data || error.message);
    }
  },
);

/**
 * Stop service
 */
export const stopService = createAsyncThunk(
  "applications/stopService",
  async ({ appId, serviceId }, { rejectWithValue }) => {
    try {
      const response = await stopServiceApi(appId, serviceId);
      return { appId, serviceId, result: response };
    } catch (error) {
      return rejectWithValue(error?.response?.data || error.message);
    }
  },
);

/**
 * Restart service
 */
export const restartService = createAsyncThunk(
  "applications/restartService",
  async ({ appId, serviceId }, { rejectWithValue }) => {
    try {
      const response = await restartServiceApi(appId, serviceId);
      return { appId, serviceId, result: response };
    } catch (error) {
      return rejectWithValue(error?.response?.data || error.message);
    }
  },
);

/**
 * Get service logs
 */
export const fetchServiceLogs = createAsyncThunk(
  "applications/fetchServiceLogs",
  async ({ appId, serviceId, options = {} }, { rejectWithValue }) => {
    try {
      const response = await getServiceLogsApi(appId, serviceId, options);
      const logs = response.data?.data?.logs ?? response.data?.logs ?? "";
      return { appId, serviceId, logs };
    } catch (error) {
      return rejectWithValue(error?.response?.data || error.message);
    }
  },
);

// ===========================================================================
// SLICE
// ===========================================================================

const initialState = {
  // Applications list
  applications: [],
  pagination: {
    page: 1,
    limit: 10,
    total: 0,
    totalPages: 0,
  },

  // Current application detail
  currentApplication: null,

  // Service logs
  serviceLogs: {},

  // Loading states
  loading: false,
  createLoading: false,
  deleteLoading: false,
  deployLoading: false,
  serviceActionLoading: {},

  // Error states
  error: null,

  // Filters
  filters: {
    deploymentType: "",
    status: "",
    environment: "",
    search: "",
  },
};

const applicationSlice = createSlice({
  name: "applications",
  initialState,
  reducers: {
    // Clear current application
    clearCurrentApplication: (state) => {
      state.currentApplication = null;
    },

    // Clear error
    clearError: (state) => {
      state.error = null;
    },

    // Update filters
    setFilters: (state, action) => {
      state.filters = { ...state.filters, ...action.payload };
    },

    // Reset filters
    resetFilters: (state) => {
      state.filters = initialState.filters;
    },

    // Update service status (for real-time updates)
    updateServiceStatus: (state, action) => {
      const { appId, serviceId, status, containerId } = action.payload;

      // Update in applications list
      const appIndex = state.applications.findIndex((a) => a.id === appId);
      if (appIndex !== -1) {
        const serviceIndex = state.applications[appIndex].services?.findIndex(
          (s) => s.id === serviceId,
        );
        if (serviceIndex !== -1) {
          state.applications[appIndex].services[serviceIndex].status = status;
          if (containerId) {
            state.applications[appIndex].services[serviceIndex].containerId =
              containerId;
          }
        }
      }

      // Update in current application
      if (state.currentApplication?.id === appId) {
        const serviceIndex = state.currentApplication.services?.findIndex(
          (s) => s.id === serviceId,
        );
        if (serviceIndex !== -1) {
          state.currentApplication.services[serviceIndex].status = status;
          if (containerId) {
            state.currentApplication.services[serviceIndex].containerId =
              containerId;
          }
        }
      }
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch applications
      .addCase(fetchApplications.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchApplications.fulfilled, (state, action) => {
        state.loading = false;
        state.applications = action.payload.data || [];
        state.pagination = action.payload.pagination || initialState.pagination;
      })
      .addCase(fetchApplications.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload?.error || "Failed to fetch applications";
      })

      // Fetch application by ID
      .addCase(fetchApplicationById.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchApplicationById.fulfilled, (state, action) => {
        state.loading = false;
        state.currentApplication = action.payload;
      })
      .addCase(fetchApplicationById.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload?.error || "Failed to fetch application";
      })

      // Create application
      .addCase(createApplication.pending, (state) => {
        state.createLoading = true;
        state.error = null;
      })
      .addCase(createApplication.fulfilled, (state, action) => {
        state.createLoading = false;
        state.applications.unshift(action.payload);
      })
      .addCase(createApplication.rejected, (state, action) => {
        state.createLoading = false;
        state.error = action.payload?.error || "Failed to create application";
      })

      // Update application
      .addCase(updateApplication.fulfilled, (state, action) => {
        const index = state.applications.findIndex(
          (app) => app.id === action.payload.id,
        );
        if (index !== -1) {
          state.applications[index] = action.payload;
        }
        if (state.currentApplication?.id === action.payload.id) {
          state.currentApplication = action.payload;
        }
      })

      // Delete application
      .addCase(deleteApplication.pending, (state) => {
        state.deleteLoading = true;
      })
      .addCase(deleteApplication.fulfilled, (state, action) => {
        state.deleteLoading = false;
        state.applications = state.applications.filter(
          (app) => app.id !== action.payload,
        );
        if (state.currentApplication?.id === action.payload) {
          state.currentApplication = null;
        }
      })
      .addCase(deleteApplication.rejected, (state, action) => {
        state.deleteLoading = false;
        state.error = action.payload?.error || "Failed to delete application";
      })

      // Deploy application
      .addCase(deployApplication.pending, (state) => {
        state.deployLoading = true;
      })
      .addCase(deployApplication.fulfilled, (state) => {
        state.deployLoading = false;
      })
      .addCase(deployApplication.rejected, (state, action) => {
        state.deployLoading = false;
        state.error = action.payload?.error || "Failed to deploy application";
      })

      // Redeploy application
      .addCase(redeployApplication.pending, (state) => {
        state.deployLoading = true;
      })
      .addCase(redeployApplication.fulfilled, (state) => {
        state.deployLoading = false;
      })
      .addCase(redeployApplication.rejected, (state, action) => {
        state.deployLoading = false;
        state.error = action.payload?.error || "Failed to redeploy application";
      })

      // Add service
      .addCase(addService.fulfilled, (state, action) => {
        const { appId, service } = action.payload;
        const index = state.applications.findIndex((app) => app.id === appId);
        if (index !== -1) {
          state.applications[index].services =
            state.applications[index].services || [];
          state.applications[index].services.push(service);
        }
        if (state.currentApplication?.id === appId) {
          state.currentApplication.services =
            state.currentApplication.services || [];
          state.currentApplication.services.push(service);
        }
      })

      // Update service
      .addCase(updateService.fulfilled, (state, action) => {
        const { appId, service } = action.payload;
        const index = state.applications.findIndex((app) => app.id === appId);
        if (index !== -1 && state.applications[index].services) {
          const sIdx = state.applications[index].services.findIndex(
            (s) => s.id === service.id,
          );
          if (sIdx !== -1) {
            state.applications[index].services[sIdx] = service;
          }
        }
        if (
          state.currentApplication?.id === appId &&
          state.currentApplication.services
        ) {
          const sIdx = state.currentApplication.services.findIndex(
            (s) => s.id === service.id,
          );
          if (sIdx !== -1) {
            state.currentApplication.services[sIdx] = service;
          }
        }
      })

      // Delete service
      .addCase(deleteService.fulfilled, (state, action) => {
        const { appId, serviceId } = action.payload;
        const index = state.applications.findIndex((app) => app.id === appId);
        if (index !== -1 && state.applications[index].services) {
          state.applications[index].services = state.applications[
            index
          ].services.filter((s) => s.id !== serviceId);
        }
        if (
          state.currentApplication?.id === appId &&
          state.currentApplication.services
        ) {
          state.currentApplication.services =
            state.currentApplication.services.filter((s) => s.id !== serviceId);
        }
      })

      // Service action (deploy, start, stop, restart)
      .addCase(deployService.pending, (state, action) => {
        const { serviceId } = action.meta.arg;
        state.serviceActionLoading[serviceId] = true;
      })
      .addCase(deployService.fulfilled, (state, action) => {
        const { serviceId } = action.payload;
        state.serviceActionLoading[serviceId] = false;
      })
      .addCase(deployService.rejected, (state, action) => {
        const { serviceId } = action.meta.arg;
        state.serviceActionLoading[serviceId] = false;
        state.error = action.payload?.error || "Failed to deploy service";
      })

      .addCase(startService.pending, (state, action) => {
        const { serviceId } = action.meta.arg;
        state.serviceActionLoading[serviceId] = true;
      })
      .addCase(startService.fulfilled, (state, action) => {
        const { serviceId } = action.payload;
        state.serviceActionLoading[serviceId] = false;
      })
      .addCase(startService.rejected, (state, action) => {
        const { serviceId } = action.meta.arg;
        state.serviceActionLoading[serviceId] = false;
        state.error = action.payload?.error || "Failed to start service";
      })

      .addCase(stopService.pending, (state, action) => {
        const { serviceId } = action.meta.arg;
        state.serviceActionLoading[serviceId] = true;
      })
      .addCase(stopService.fulfilled, (state, action) => {
        const { serviceId } = action.payload;
        state.serviceActionLoading[serviceId] = false;
      })
      .addCase(stopService.rejected, (state, action) => {
        const { serviceId } = action.meta.arg;
        state.serviceActionLoading[serviceId] = false;
        state.error = action.payload?.error || "Failed to stop service";
      })

      .addCase(restartService.pending, (state, action) => {
        const { serviceId } = action.meta.arg;
        state.serviceActionLoading[serviceId] = true;
      })
      .addCase(restartService.fulfilled, (state, action) => {
        const { serviceId } = action.payload;
        state.serviceActionLoading[serviceId] = false;
      })
      .addCase(restartService.rejected, (state, action) => {
        const { serviceId } = action.meta.arg;
        state.serviceActionLoading[serviceId] = false;
        state.error = action.payload?.error || "Failed to restart service";
      })

      // Fetch service logs
      .addCase(fetchServiceLogs.fulfilled, (state, action) => {
        const { serviceId, logs } = action.payload;
        state.serviceLogs[serviceId] = logs;
      })
      .addCase(fetchServiceLogs.rejected, (state, action) => {
        const { serviceId } = action.meta.arg;
        state.error =
          action.payload?.error ||
          action.payload?.message ||
          "Failed to fetch logs";
        // Optionally keep the last valid logs and don't wipe them
        // state.serviceLogs[serviceId] = "Error fetching logs: " + (action.payload?.error || 'Unknown error');
      });
  },
});

export const {
  clearCurrentApplication,
  clearError,
  setFilters,
  resetFilters,
  updateServiceStatus,
} = applicationSlice.actions;

export default applicationSlice.reducer;
